import { useAppSelector } from "app/redux-store-interactions";
import { useHidden } from "contexts/hidden";
import { useHighlightCollections } from "contexts/highlightCollections";
import { useHighlighted } from "contexts/highlighted";
import { useSelectionBasket } from "contexts/selectionBasket";
import { useShown } from "contexts/shown";
import { selectCoordinates } from "features/coordinates";
import { selectMeasureEntities, selectObjectMeasureIds } from "features/measure/measureSlice";
import { selectOutlineLasers } from "features/outlineLaser/outlineLaserSlice";
import { selectPointLines } from "features/pointLine/pointLineSlice";
import { ObjectVisibility, selectClippingPlanes, selectDefaultVisibility } from "features/render";
import { useObjectTreeHidden } from "hooks/useObjectTreeHidden";

/**
 * Returns `true` when there is some view state that {@link useResetView} would clear
 * (selections, hidden objects, clipping planes, measurements, etc.). Use this to drive
 * the disabled state of "Reset view" / "Clear view" buttons so they only light up when
 * there is something to reset.
 *
 * Keep this in sync with the state that `useResetView` actually clears.
 */
export function useCanResetView(): boolean {
    const highlighted = useHighlighted();
    const hidden = useHidden();
    const selectionBasket = useSelectionBasket();
    const highlightCollections = useHighlightCollections();
    const shown = useShown();
    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    const clippingPlanes = useAppSelector(selectClippingPlanes);
    const coordinates = useAppSelector(selectCoordinates);
    const lasers = useAppSelector(selectOutlineLasers);
    const pointLines = useAppSelector(selectPointLines);
    const dimensions = useAppSelector(selectMeasureEntities);
    // The picks, not just the result: a reset clears both, and a lone pick survives a mode exit
    // with nothing else to light the button up - x-ray is switched off on the way out, and the
    // highlight collection only holds the pair once both are picked.
    const objectMeasureIds = useAppSelector(selectObjectMeasureIds);
    const { objectTreeHiddenCount, pdfObjectIds } = useObjectTreeHidden();

    // A reset leaves Procore 2D sheets hidden, so they must not light the button up.
    // Until discovery resolves it clears every hidden id, and so has work to do.
    const hasResettableHidden = pdfObjectIds ? objectTreeHiddenCount > 0 : hidden.idArr.length > 0;

    return (
        highlighted.idArr.length > 0 ||
        hasResettableHidden ||
        selectionBasket.idArr.length > 0 ||
        shown.ids.size > 0 ||
        Object.values(highlightCollections).some((collection) => collection.idArr.length > 0) ||
        defaultVisibility !== ObjectVisibility.Neutral ||
        clippingPlanes.enabled ||
        clippingPlanes.planes.length > 0 ||
        coordinates.length > 0 ||
        lasers.length > 0 ||
        pointLines.some((line) => line.points.length > 0) ||
        dimensions.some((set) => set.length > 0) ||
        objectMeasureIds.length > 0
    );
}
